import React from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ThumbsUpIcon as HugeThumbsUp,
  ThumbsDownIcon as HugeThumbsDown,
  Comment02Icon as HugeComment,
  Share01Icon as HugeShare,
  SunIcon as HugeSun,
  MoonIcon as HugeMoon,
  ViewIcon as HugeView,
  ViewOffSlashIcon as HugeViewOff,
  ArrowRight01Icon as HugeArrowRight,
  ArrowDown01Icon as HugeArrowDown,
  ArrowUp01Icon as HugeArrowUp,
} from "@hugeicons/core-free-icons";
import { Settings } from "lucide-react";

/**
 * Sun glyph for light theme (`currentColor`).
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function ThemeSunIcon({ size = 20, className } = {}) {
  return (
    <HugeiconsIcon
      icon={HugeSun}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={2}
    />
  );
}

/**
 * Moon glyph for dark themes (`currentColor`).
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function ThemeMoonIcon({ size = 20, className } = {}) {
  return (
    <HugeiconsIcon
      icon={HugeMoon}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={2}
    />
  );
}

/**
 * Thumbs-up icon for likes.
 * @param {Object} [props]
 * @param {boolean} [props.filled] - Visual emphasis when liked (thicker stroke)
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function ThumbsUpIcon({ filled = false, size = 18, className }) {
  return (
    <HugeiconsIcon
      icon={HugeThumbsUp}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={filled ? 2 : 1.5}
    />
  );
}

/**
 * Thumbs-down icon for dislikes.
 * @param {Object} [props]
 * @param {boolean} [props.filled]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function ThumbsDownIcon({ filled = false, size = 18, className }) {
  return (
    <HugeiconsIcon
      icon={HugeThumbsDown}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={filled ? 2 : 1.5}
    />
  );
}

/**
 * Speech-bubble icon for comments / replies.
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function CommentIcon({ size = 18, className }) {
  return (
    <HugeiconsIcon
      icon={HugeComment}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={1.5}
    />
  );
}

/**
 * Share icon.
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function ShareIcon({ size = 18, className }) {
  return (
    <HugeiconsIcon
      icon={HugeShare}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={1.5}
    />
  );
}

/**
 * Eye icon for revealing a password field.
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function ViewIcon({ size = 18, className }) {
  return (
    <HugeiconsIcon
      icon={HugeView}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={2}
    />
  );
}

/**
 * Eye-off icon for hiding a password field.
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function ViewOffIcon({ size = 18, className }) {
  return (
    <HugeiconsIcon
      icon={HugeViewOff}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={2}
    />
  );
}

/**
 * Right chevron for send actions (`currentColor`).
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function SendIcon({ size = 18, className } = {}) {
  return (
    <HugeiconsIcon
      icon={HugeArrowRight}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={2.5}
    />
  );
}

/**
 * Disclosure chevron for expand / collapse toggles (`currentColor`).
 * @param {Object} [props]
 * @param {boolean} [props.expanded]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function DisclosureChevronIcon({ expanded = false, size = 16, className } = {}) {
  return (
    <HugeiconsIcon
      icon={expanded ? HugeArrowUp : HugeArrowDown}
      size={size}
      color="currentColor"
      className={className}
      strokeWidth={2}
    />
  );
}

/**
 * Globe glyph for public / feed meta (`currentColor`).
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function GlobeIcon({ size = 11, className } = {}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.5 3 2.5 14 0 17M12 3.5c-2.5 3-2.5 14 0 17" />
    </svg>
  );
}

/**
 * Plus glyph for compact connect actions (`currentColor`).
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function PlusIcon({ size = 13, className } = {}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/**
 * Bell glyph for notifications (`currentColor`).
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function NotificationBellIcon({ size = 20, className } = {}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M6 16V11a6 6 0 0112 0v5l1.5 2h-15z" />
      <path d="M10 20a2 2 0 004 0" />
    </svg>
  );
}

/**
 * Gear icon for settings (`currentColor`).
 * @param {Object} [props]
 * @param {number} [props.size]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function SettingsIcon({ size = 17, className } = {}) {
  return (
    <Settings
      size={size}
      className={className}
      strokeWidth={2}
      aria-hidden
    />
  );
}
